import { readdirSync, readFileSync, statSync } from 'node:fs';
import { join } from 'node:path';
import { LITERAL_SECRET_PATTERNS } from '@augmented/core/provisioning/mcp-secret-lint.js';

/**
 * ENG-5901 (Phase 1 of ADR-0018) — operator-side audit for literal
 * secrets in on-disk agent config. Sibling to `agt audit subagents`
 * (ENG-5897) and `agt audit mcp-render` (ENG-5922).
 *
 * Why this exists (vs. the runtime lint in @augmented/core's
 * mcp-secret-lint): the runtime lint is a *contributor-side* gate that
 * rejects a bad write at provision/sync time. This audit is the
 * *operator-side* counterpart that walks what's already on disk:
 *
 *   - `~/.augmented/<agent>/provision/.mcp.json`
 *   - `~/.augmented/<agent>/project/.mcp.json`
 *   - `~/.augmented/<agent>/.env.integrations`
 *
 * It catches three things the runtime lint structurally can't:
 *   1. historical leaks written before the lint existed,
 *   2. drift where a writer was bypassed,
 *   3. world-readable secret files (mode 0644 — the audit-observed
 *      default for `.mcp.json`; `.env.integrations` should be 0600).
 *
 * Strictly read-only: `fs.readFileSync` / `fs.statSync` only, never a
 * write. `--strict` exits 2 so CI can gate a deploy on a clean audit.
 *
 * Secret VALUES never appear in findings or output — only the file,
 * agent, server, and field name. The whole point is not to leak.
 */

/** Field-name shapes that conventionally hold a secret. */
export const SECRET_KEY_NAME_RE = /TOKEN|KEY|SECRET|BEARER|PASSWORD/i;

/** A value is considered safely templated if it defers to a `${VAR}`
 *  placeholder (e.g. `${SLACK_BOT_TOKEN}` or `Bearer ${API_KEY}`). The
 *  literal secret never reaches disk in that case — Claude Code
 *  substitutes it at MCP-launch time. */
function isTemplated(value: string): boolean {
  return value.includes('${');
}

/** A value matching one of the shared literal-secret shapes (xoxb-/xapp-/
 *  tlk_/ak_/Telegram/JWT — same list the runtime lint enforces) is a leak
 *  regardless of what the field is called. Catches e.g. a literal
 *  `Authorization: Bearer eyJ…` whose key name carries no secret hint
 *  (CodeRabbit #1731). */
function matchesKnownSecretShape(value: string): boolean {
  return LITERAL_SECRET_PATTERNS.some(({ re }) => re.test(value));
}

export type SecretAuditKind =
  | 'literal-secret-in-mcp'
  | 'world-readable-secret-file';

export interface SecretAuditFinding {
  kind: SecretAuditKind;
  /** Absolute path of the offending file. */
  file: string;
  /** Agent code_name (the directory under the augmented root). */
  agent: string;
  /** MCP server key — only for `literal-secret-in-mcp`. */
  server?: string;
  /** env key / header name — only for `literal-secret-in-mcp`. */
  field?: string;
  /** Whether the literal sat in an `env` value or a `headers` value. */
  location?: 'env' | 'header';
  /** Octal mode (e.g. `0644`) — only for `world-readable-secret-file`. */
  mode?: string;
}

interface McpServerEntry {
  env?: Record<string, unknown>;
  headers?: Record<string, unknown>;
}

/** A secret file should be 0600. Any group/other permission bit is a leak. */
function isWorldReadable(mode: number): boolean {
  return (mode & 0o077) !== 0;
}

function octal(mode: number): string {
  return `0${(mode & 0o777).toString(8)}`;
}

function scanMcpFile(
  file: string,
  agent: string,
  findings: SecretAuditFinding[],
): void {
  let mode: number | null = null;
  try {
    mode = statSync(file).mode;
  } catch {
    return; // missing file — nothing to audit
  }
  if (mode !== null && isWorldReadable(mode)) {
    findings.push({ kind: 'world-readable-secret-file', file, agent, mode: octal(mode) });
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(readFileSync(file, 'utf-8'));
  } catch {
    return; // unreadable / malformed JSON — mode finding (if any) still stands
  }
  const servers = (parsed as { mcpServers?: Record<string, unknown> })?.mcpServers;
  if (typeof servers !== 'object' || servers === null) return;

  for (const [server, raw] of Object.entries(servers)) {
    if (typeof raw !== 'object' || raw === null) continue;
    const entry = raw as McpServerEntry;
    const blocks: Array<[Record<string, unknown> | undefined, 'env' | 'header']> = [
      [entry.env, 'env'],
      [entry.headers, 'header'],
    ];
    for (const [block, location] of blocks) {
      if (!block) continue;
      for (const [field, value] of Object.entries(block)) {
        if (typeof value !== 'string' || value.length === 0) continue;
        if (isTemplated(value)) continue;
        // Union of two detectors (CodeRabbit #1731): a secret-NAMED field
        // holding any untemplated literal (drift catch — covers tokens
        // with no recognisable prefix), OR any field whose VALUE matches a
        // known secret shape (covers e.g. `Authorization: Bearer eyJ…`
        // where the key name carries no hint). Replacing the name check
        // entirely would regress the first class, so both run.
        if (!SECRET_KEY_NAME_RE.test(field) && !matchesKnownSecretShape(value)) continue;
        findings.push({ kind: 'literal-secret-in-mcp', file, agent, server, field, location });
      }
    }
  }
}

/** `.env.integrations` legitimately holds raw secrets, so we only check
 *  its file mode (must be 0600), never its contents. Under ENG-9350
 *  (`spawn-inject-channel-secrets`) the channel bot tokens are delivered via
 *  the `tmux -e` spawn env instead and are absent from this file — the 0600
 *  invariant still applies to whatever integration secrets remain. */
function scanEnvIntegrations(
  file: string,
  agent: string,
  findings: SecretAuditFinding[],
): void {
  let mode: number;
  try {
    mode = statSync(file).mode;
  } catch {
    return;
  }
  if (isWorldReadable(mode)) {
    findings.push({ kind: 'world-readable-secret-file', file, agent, mode: octal(mode) });
  }
}

/**
 * Walk every agent under `augmentedRoot` (default `~/.augmented`) and
 * return findings. Pure read-only; takes the root as a parameter so
 * tests can point it at a fixture.
 */
export function auditSecretLeaks(augmentedRoot: string): SecretAuditFinding[] {
  const findings: SecretAuditFinding[] = [];
  let agents: string[];
  try {
    agents = readdirSync(augmentedRoot);
  } catch {
    return findings; // root missing — nothing provisioned locally
  }
  for (const agent of agents) {
    const agentDir = join(augmentedRoot, agent);
    try {
      if (!statSync(agentDir).isDirectory()) continue;
    } catch {
      continue;
    }
    scanMcpFile(join(agentDir, 'provision', '.mcp.json'), agent, findings);
    scanMcpFile(join(agentDir, 'project', '.mcp.json'), agent, findings);
    scanEnvIntegrations(join(agentDir, '.env.integrations'), agent, findings);
  }
  return findings;
}

/** Single-line structured log entry, secret-free. Mirrors the
 *  `agt audit subagents` stderr convention. */
export function formatSecretFinding(f: SecretAuditFinding): string {
  const parts = [
    '[audit-secrets]',
    `kind=${f.kind}`,
    `agent=${f.agent}`,
    `file=${f.file}`,
  ];
  if (f.server) parts.push(`server=${f.server}`);
  if (f.field) parts.push(`field=${f.field}`);
  if (f.location) parts.push(`location=${f.location}`);
  if (f.mode) parts.push(`mode=${f.mode}`);
  return parts.join(' ');
}
